import usePageMetadata from '@/hooks/usePageMetadata';
import useSelectedNetworks from '@/hooks/useSelectedNetworks';
import type { Network } from '@/types';

export interface UseNetworkFilterResult {
  /** Valid selected network ids parsed from `?networks=`, in URL order, deduped and authorized. */
  selectedNetworkIds: string[];
  /** Replaces the `?networks=` selection (validates, dedupes, caps at MAX_NETWORK_IDS, min 1). */
  setSelectedNetworkIds: (networkIds: string[]) => void;
  /** `true` while fewer than MAX_NETWORK_IDS networks are selected. */
  canSelectMore: boolean;
  /** `true` when the network multi-select UI should render (query-param mode active and user has > 1 network). */
  showNetworkMultiSelect: boolean;
}

/**
 * Owns the decision of whether the query-param filter mode is active and drives the network
 * multi-select UI accordingly.
 *
 * Internally determines whether the query-param URL shape is in use on the content performance
 * page. Consumers receive the network selection state and a `showNetworkMultiSelect` boolean —
 * they never need to branch on the routing mode themselves.
 * @param networks - The user's authorized networks for the current app type.
 * @returns Network selection state and a visibility flag for the network multi-select UI.
 */
const useNetworkFilter = (networks: Network[] | undefined): UseNetworkFilterResult => {
  const { page: pageType } = usePageMetadata();
  const isQueryParamMode = pageType === 'content-performance';
  const { selectedNetworkIds, setSelectedNetworkIds, canSelectMore } = useSelectedNetworks(
    networks,
    isQueryParamMode
  );

  return {
    selectedNetworkIds,
    setSelectedNetworkIds,
    canSelectMore,
    showNetworkMultiSelect: isQueryParamMode && (networks ?? []).length > 1,
  };
};

export default useNetworkFilter;
